import type { AggregateStats, GeneratedMeta, SkillStatus } from "../lib/types";
import { useFilters } from "../store/filters";

export function KpiStrip({ stats, meta }: { stats: AggregateStats; meta?: GeneratedMeta }) {
  const statuses = useFilters((s) => s.statuses);
  const health = stats.statusCounts ?? { healthy: 0, warning: 0, critical: 0 };
  const total = Math.max(1, health.healthy + health.warning + health.critical);

  return (
    <div className="kpi-strip grid border-b border-fg" style={{ gridTemplateColumns: "1.4fr 1fr 1.6fr 0.9fr" }} aria-label="Aggregate quality">
      <Cell k="// Avg combined" big>
        <span className="text-accent">{stats.average}</span>
        <span className="font-mono text-[14px] text-muted font-normal ml-1">%</span>
        <span className={`font-mono text-[12px] ml-2 ${stats.trendDelta > 0 ? "text-good" : stats.trendDelta < 0 ? "text-bad" : "text-muted"}`}>
          {stats.trendDelta > 0 ? "+" : ""}{stats.trendDelta}
        </span>
      </Cell>
      <Cell k="// Above 85" bigClass="text-good">{stats.above85}</Cell>
      <Cell k="// Avg structural">
        {stats.averageStructural}
        <span className="font-mono text-[12px] text-muted font-normal ml-1">%</span>
        {stats.withLlm > 0 ? (
          <span className="block font-mono text-[9px] uppercase tracking-[0.1em] text-good font-medium mt-1">
            {stats.withLlm} w/ LLM layer
          </span>
        ) : meta && !meta.llmAvailable ? (
          <span className="block font-mono text-[9px] uppercase tracking-[0.1em] text-warn font-medium mt-1">
            LLM off · run combined-score
          </span>
        ) : null}
      </Cell>
      <div className="p-4 border-r border-rule last:border-r-0">
        <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted font-medium">// Health breakdown</div>
        <div className="flex items-baseline gap-4 mt-1.5">
          <HealthCount label="Healthy" value={health.healthy} cls="text-good" dot="bg-good" active={statuses.healthy} onClick={() => filterHealth("healthy")} />
          <HealthCount label="Warning" value={health.warning} cls="text-warn" dot="bg-warn" active={statuses.warning} onClick={() => filterHealth("warning")} />
          <HealthCount label="Critical" value={health.critical} cls="text-bad" dot="bg-bad" active={statuses.critical} onClick={() => filterHealth("critical")} />
        </div>
        <div className="flex h-2 mt-2.5 overflow-hidden border border-fg" role="img" aria-label="Health distribution">
          <span className="bg-good h-full" style={{ width: `${(health.healthy / total) * 100}%` }} />
          <span className="bg-warn h-full" style={{ width: `${(health.warning / total) * 100}%` }} />
          <span className="bg-bad h-full" style={{ width: `${(health.critical / total) * 100}%` }} />
        </div>
      </div>
    </div>
  );
}

function filterHealth(key: SkillStatus) {
  const { statuses } = useFilters.getState();
  const onlyThis =
    statuses[key] &&
    !(["healthy", "warning", "critical"] as const).some((k) => k !== key && statuses[k]);
  if (onlyThis) {
    useFilters.setState({
      statuses: { healthy: true, warning: true, critical: true },
    });
  } else {
    useFilters.setState({
      statuses: { healthy: key === "healthy", warning: key === "warning", critical: key === "critical" },
      sort: "score-asc",
    });
  }
  window.dispatchEvent(new Event("filters-updated"));
}

function HealthCount({
  label, value, cls, dot, active, onClick,
}: { label: string; value: number; cls: string; dot: string; active?: boolean; onClick?: () => void }) {
  return (
    <button type="button" onClick={onClick} className={`flex flex-col text-left ${active ? "opacity-100" : "opacity-70 hover:opacity-100"}`}>
      <div className="flex items-center gap-1.5">
        <span className={`w-2 h-2 rounded-full ${dot}`} aria-hidden="true" />
        <span className={`font-display font-extrabold text-t-26 leading-none tabular-nums ${cls}`}>{value}</span>
      </div>
      <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-muted font-medium mt-1">{label}</span>
    </button>
  );
}

function Cell({ k, children, big, bigClass = "" }: { k: string; children: React.ReactNode; big?: boolean; bigClass?: string }) {
  return (
    <div className="p-4 border-r border-rule last:border-r-0">
      <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted font-medium">{k}</div>
      <div className={`font-display font-extrabold leading-[1] tracking-[-0.04em] mt-1.5 tabular-nums ${big ? "text-t-72" : "text-t-56"} ${bigClass}`}>
        {children}
      </div>
    </div>
  );
}